
import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { CheckCircle, XCircle, Clock, User, Calendar, Building2, Printer, Trash2, Edit, Save, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface PrinterManufacturerSuggestion {
  id: string;
  type: 'printer' | 'manufacturer';
  name: string;
  manufacturer_name?: string;
  series_name?: string;
  description?: string;
  build_plate_dimensions?: string;
  status: 'pending' | 'approved' | 'rejected';
  submitted_by: string;
  created_at: string;
  feedback?: string;
  manufacturer_id?: number;
  series_id?: number;
}

interface PrinterManufacturerSuggestionModalProps {
  suggestion: PrinterManufacturerSuggestion;
  isOpen: boolean;
  onClose: () => void;
  onApprove: (suggestionId: string) => void;
  onReject: (suggestionId: string, feedback: string) => void;
  onDelete: (suggestionId: string) => void;
  onUpdate: () => void;
}

const PrinterManufacturerSuggestionModal: React.FC<PrinterManufacturerSuggestionModalProps> = ({
  suggestion,
  isOpen,
  onClose,
  onApprove,
  onReject,
  onDelete,
  onUpdate
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedSuggestion, setEditedSuggestion] = useState(suggestion);
  const [saving, setSaving] = useState(false);
  const [rejectDialog, setRejectDialog] = useState({ open: false, feedback: '' });

  const handleSaveEdits = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from("printer_manufacturer_suggestions")
        .update({
          name: editedSuggestion.name,
          manufacturer_name: editedSuggestion.manufacturer_name,
          series_name: editedSuggestion.series_name,
          description: editedSuggestion.description,
          build_plate_dimensions: editedSuggestion.build_plate_dimensions,
          updated_at: new Date().toISOString()
        })
        .eq("id", suggestion.id);

      if (error) throw error;

      toast.success("Changes saved successfully");
      setIsEditing(false);
      onUpdate();
    } catch (error) {
      console.error('Error saving suggestion:', error);
      toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'approved':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'rejected':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status: string) => {
    const baseClasses = "flex items-center gap-1";
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className={`${baseClasses} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`}>
          <Clock className="h-3 w-3" />
          Pending
        </Badge>;
      case 'approved':
        return <Badge variant="outline" className={`${baseClasses} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`}>
          <CheckCircle className="h-3 w-3" />
          Approved
        </Badge>;
      case 'rejected':
        return <Badge variant="outline" className={`${baseClasses} bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`}>
          <XCircle className="h-3 w-3" />
          Rejected
        </Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const handleReject = () => {
    if (!rejectDialog.feedback.trim()) {
      toast.error("Please provide feedback for rejection");
      return;
    }
    onReject(suggestion.id, rejectDialog.feedback);
    setRejectDialog({ open: false, feedback: '' });
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="flex items-center gap-2">
                  {suggestion.type === 'printer' ? <Printer className="h-5 w-5" /> : <Building2 className="h-5 w-5" />}
                  {suggestion.name}
                </DialogTitle>
                <DialogDescription className="flex items-center gap-4 mt-2">
                  <span className="flex items-center gap-1">
                    <User className="h-4 w-4" />
                    User ID: {suggestion.submitted_by?.slice(0, 8)}...
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    {formatDistanceToNow(new Date(suggestion.created_at), { addSuffix: true })}
                  </span>
                  <span className="capitalize">
                    {suggestion.type} suggestion
                  </span>
                </DialogDescription>
              </div>
              <div className="flex items-center gap-2">
                {getStatusBadge(suggestion.status)}
              </div>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-hidden">
            <ScrollArea className="h-full pr-4">
              <div className="space-y-6">
                {/* Basic Information */}
                <div>
                  <h3 className="font-semibold mb-3">Basic Information</h3>
                  <div className="bg-muted/50 p-4 rounded-lg space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name">Name</Label>
                        {isEditing ? (
                          <Input
                            id="name"
                            value={editedSuggestion.name}
                            onChange={(e) => setEditedSuggestion({ ...editedSuggestion, name: e.target.value })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-sm">{suggestion.name}</p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="type">Type</Label>
                        <p className="mt-1 text-sm capitalize">{suggestion.type}</p>
                      </div>
                    </div>

                    {suggestion.type === 'printer' && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="manufacturer">Manufacturer</Label>
                          {isEditing ? (
                            <Input
                              id="manufacturer"
                              value={editedSuggestion.manufacturer_name || ''}
                              onChange={(e) => setEditedSuggestion({ ...editedSuggestion, manufacturer_name: e.target.value })}
                              className="mt-1"
                            />
                          ) : (
                            <p className="mt-1 text-sm">{suggestion.manufacturer_name || 'N/A'}</p>
                          )}
                        </div>
                        <div>
                          <Label htmlFor="series">Series</Label>
                          {isEditing ? (
                            <Input
                              id="series"
                              value={editedSuggestion.series_name || ''}
                              onChange={(e) => setEditedSuggestion({ ...editedSuggestion, series_name: e.target.value })}
                              className="mt-1"
                            />
                          ) : (
                            <p className="mt-1 text-sm">{suggestion.series_name || 'N/A'}</p>
                          )}
                        </div>
                      </div>
                    )}

                    {suggestion.build_plate_dimensions && (
                      <div>
                        <Label htmlFor="buildPlate">Build Plate Dimensions</Label>
                        {isEditing ? (
                          <Input
                            id="buildPlate"
                            value={editedSuggestion.build_plate_dimensions || ''}
                            onChange={(e) => setEditedSuggestion({ ...editedSuggestion, build_plate_dimensions: e.target.value })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-sm">{suggestion.build_plate_dimensions}</p>
                        )}
                      </div>
                    )}

                    <div>
                      <Label htmlFor="description">Description</Label>
                      {isEditing ? (
                        <Textarea
                          id="description"
                          value={editedSuggestion.description || ''}
                          onChange={(e) => setEditedSuggestion({ ...editedSuggestion, description: e.target.value })}
                          className="mt-1 min-h-[100px]"
                        />
                      ) : (
                        <p className="mt-1 text-sm">{suggestion.description || 'No description provided'}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Feedback (if rejected) */}
                {suggestion.feedback && (
                  <div>
                    <h3 className="font-semibold mb-2">Admin Feedback</h3>
                    <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border border-red-200 dark:border-red-800">
                      <p className="text-sm">{suggestion.feedback}</p>
                    </div>
                  </div>
                )}

                {/* Raw Data (for debugging) */}
                <details className="text-xs">
                  <summary className="cursor-pointer font-medium mb-2">Raw Data (Debug)</summary>
                  <pre className="bg-muted p-4 rounded-lg overflow-auto text-xs">
                    {JSON.stringify(suggestion, null, 2)}
                  </pre>
                </details>
              </div>
            </ScrollArea>
          </div>

          <Separator />

          <DialogFooter className="flex justify-between">
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
              {suggestion.status === 'pending' && !isEditing && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsEditing(true);
                    setEditedSuggestion(suggestion);
                  }}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              )}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="border-destructive text-destructive hover:bg-destructive/10">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Suggestion</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete this suggestion? This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => onDelete(suggestion.id)}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>

            <div className="flex gap-2">
              {isEditing ? (
                <>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setIsEditing(false);
                      setEditedSuggestion(suggestion);
                    }}
                  >
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleSaveEdits}
                    disabled={saving}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </>
              ) : suggestion.status === 'pending' && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => setRejectDialog({ open: true, feedback: '' })}
                    className="border-destructive text-destructive hover:bg-destructive/10"
                  >
                    Reject
                  </Button>
                  <Button
                    onClick={() => onApprove(suggestion.id)}
                  >
                    Approve
                  </Button>
                </>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog.open} onOpenChange={(open) => setRejectDialog({ ...rejectDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Suggestion</DialogTitle>
            <DialogDescription>
              Please provide feedback on why this suggestion is being rejected.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Textarea
              placeholder="Please provide rejection reason"
              value={rejectDialog.feedback}
              onChange={(e) => setRejectDialog({ ...rejectDialog, feedback: e.target.value })}
              className="min-h-[100px]"
            />
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setRejectDialog({ open: false, feedback: '' })}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={!rejectDialog.feedback.trim()}
            >
              Reject Suggestion
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PrinterManufacturerSuggestionModal;
